import * as Sentry from "@sentry/core";
import type { Tuple } from "@litres-libs/types";
import first from "lodash/first";
import isArray from "lodash/isArray";
import isObject from "lodash/isObject";
import last from "lodash/last";

type Tags = Record<string, string | number | boolean | undefined>;

type ContextProperties = Tuple<string, Record<string, unknown>>;

export interface ILogErrorToSentryParams {
  logLevel?: "fatal" | "error" | "warning" | "debug";
  contextProperties?: ContextProperties;
  tags?: Tags;
}

/**
 * Отправляет объект ошибки в систему отлова ошибок Sentry (https://sentry.litres.io/organizations/litres/issues/?project=56).
 *
 * @deprecated Use @litres/errors instead
 * @example
 * logErrorToSentry(
 *   'My error message', // error text,
 *   {
 *      contextProperties: ['params', { user_type: 'abc', country: 'uk' }], // context props,
 *      tags: { tag1: 'tag1_value' },
 *      logLevel: logLevel.Error, // optionsl
 *   }
 * );
 */
export function logErrorToSentry<ErrorT extends Error>(
  error: string | ErrorT,
  params: ILogErrorToSentryParams = {}
) {
  const { contextProperties, logLevel = "error", tags } = params;
  const resultError = typeof error === "string" ? new Error(error) : error;

  if (!Sentry || !Sentry.isEnabled()) {
    return;
  }

  if (contextProperties && isArray(contextProperties)) {
    Sentry.setContext(first(contextProperties), last(contextProperties));
  }

  Sentry.withScope(scope => {
    if (isObject(tags)) {
      scope.setTags(tags);
    }

    scope.setLevel(logLevel);
    Sentry.captureException(resultError);
  });
}

export const sentryBreadcrumb = Sentry.addBreadcrumb;
